import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
}

interface ParticleNetworkConfig {
  particleCount?: number
  maxParticles?: number
  minParticles?: number
  particleSpeed?: number
  particleRadius?: number
  connectRadius?: number
  colors?: {
    bg?: string
    particle?: string
    line?: string
  }
  pauseWhenHidden?: boolean
  adaptive?: boolean
  debug?: boolean
}

const defaultConfig: Required<ParticleNetworkConfig> = {
  particleCount: 80,
  maxParticles: 120,
  minParticles: 40,
  particleSpeed: 0.3,
  particleRadius: 2,
  connectRadius: 150,
  colors: {
    bg: 'auto',
    particle: 'auto',
    line: 'auto'
  },
  pauseWhenHidden: true,
  adaptive: true,
  debug: false
}

export function ParticleNetwork({ 
  className,
  config = {},
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & { config?: ParticleNetworkConfig }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>(0)
  const particlesRef = useRef<Particle[]>([])
  const gridRef = useRef<Map<string, Particle[]>>(new Map())
  const performanceRef = useRef({
    frameCount: 0,
    lastTime: performance.now(),
    avgFrameTime: 16,
    adaptiveCheckTime: performance.now()
  })
  
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isReducedMotion, setIsReducedMotion] = useState(false)
  const [canvasSupported, setCanvasSupported] = useState(true)
  
  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config, colors: { ...defaultConfig.colors, ...config.colors } }), [config])
  const [currentParticleCount, setCurrentParticleCount] = useState(mergedConfig.particleCount)

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }
    checkDarkMode()
    
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    
    return () => observer.disconnect()
  }, [])

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setIsReducedMotion(mediaQuery.matches)
    
    const handler = (e: MediaQueryListEvent) => setIsReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handler)
    
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Check canvas support
  useEffect(() => {
    const canvas = document.createElement('canvas')
    setCanvasSupported(!!(canvas.getContext && canvas.getContext('2d')))
  }, [])

  // Get colors based on theme
  const getColors = useCallback(() => {
    const colors = mergedConfig.colors
    
    if (isDarkMode) {
      return {
        bg: colors.bg === 'auto' ? 'transparent' : colors.bg,
        particle: colors.particle === 'auto' ? 'hsl(190 92% 48% / 0.5)' : colors.particle,
        line: colors.line === 'auto' ? 'hsl(190 92% 48% / 0.15)' : colors.line
      }
    } else {
      return {
        bg: colors.bg === 'auto' ? 'transparent' : colors.bg,
        particle: colors.particle === 'auto' ? 'hsl(222 47% 11% / 0.3)' : colors.particle,
        line: colors.line === 'auto' ? 'hsl(222 47% 11% / 0.1)' : colors.line
      }
    }
  }, [isDarkMode, mergedConfig])

  // Initialize particles - memoize to prevent re-creation on every render
  const initParticles = useCallback((width: number, height: number, forceNew: boolean = false) => {
    // Only create new particles if forced or if we don't have any
    if (!forceNew && particlesRef.current.length > 0) {
      return;
    }
    
    const particles: Particle[] = []
    const area = width * height
    const scaledCount = Math.floor(currentParticleCount * (area / (1920 * 1080)))
    
    for (let i = 0; i < scaledCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * mergedConfig.particleSpeed,
        vy: (Math.random() - 0.5) * mergedConfig.particleSpeed,
        radius: mergedConfig.particleRadius
      })
    }
    
    particlesRef.current = particles
  }, [currentParticleCount, mergedConfig])

  // Spatial hash functions
  const getCellKey = (x: number, y: number, cellSize: number) => {
    const cellX = Math.floor(x / cellSize)
    const cellY = Math.floor(y / cellSize)
    return `${cellX},${cellY}`
  }

  const updateSpatialGrid = useCallback((particles: Particle[], cellSize: number) => {
    const grid = new Map<string, Particle[]>()
    
    particles.forEach(particle => {
      const key = getCellKey(particle.x, particle.y, cellSize)
      if (!grid.has(key)) {
        grid.set(key, [])
      }
      grid.get(key)!.push(particle)
    })
    
    gridRef.current = grid
  }, [])

  const getNeighbors = useCallback((particle: Particle, cellSize: number): Particle[] => {
    const neighbors: Particle[] = []
    const cellX = Math.floor(particle.x / cellSize)
    const cellY = Math.floor(particle.y / cellSize)
    
    // Check surrounding cells
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx},${cellY + dy}`
        const cellParticles = gridRef.current.get(key)
        if (cellParticles) {
          neighbors.push(...cellParticles)
        }
      }
    }
    
    return neighbors
  }, [])

  // Update particle positions
  const updateParticles = useCallback((width: number, height: number, deltaTime: number) => {
    const dt = Math.min(deltaTime / 16, 2) // Normalize to 60fps, cap at 2x speed
    
    particlesRef.current.forEach(particle => {
      particle.x += particle.vx * dt
      particle.y += particle.vy * dt
      
      // Wrap around edges (torus topology)
      if (particle.x < 0) particle.x = width
      if (particle.x > width) particle.x = 0
      if (particle.y < 0) particle.y = height
      if (particle.y > height) particle.y = 0
    })
  }, [])

  // Adaptive quality
  const updateAdaptiveQuality = useCallback(() => {
    if (!mergedConfig.adaptive) return
    
    const perf = performanceRef.current
    const now = performance.now()
    
    if (now - perf.adaptiveCheckTime > 1000) {
      if (perf.avgFrameTime > 22 && currentParticleCount > mergedConfig.minParticles) {
        // Reduce particles
        const newCount = Math.max(
          Math.floor(currentParticleCount * 0.85),
          mergedConfig.minParticles
        )
        setCurrentParticleCount(newCount)
      } else if (perf.avgFrameTime < 12 && currentParticleCount < mergedConfig.maxParticles) {
        // Increase particles
        const newCount = Math.min(
          Math.floor(currentParticleCount * 1.1),
          mergedConfig.maxParticles
        )
        setCurrentParticleCount(newCount)
      }
      
      perf.adaptiveCheckTime = now
    }
  }, [currentParticleCount, mergedConfig])

  // Render frame
  const render = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const colors = getColors()
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height)
    
    // Update spatial grid
    const cellSize = mergedConfig.connectRadius
    updateSpatialGrid(particlesRef.current, cellSize)
    
    // Draw connections
    ctx.strokeStyle = colors.line || 'rgba(0,0,0,0.1)'
    ctx.lineWidth = 1
    
    particlesRef.current.forEach((particle) => {
      const neighbors = getNeighbors(particle, cellSize)
      
      neighbors.forEach(neighbor => {
        if (neighbor === particle) return
        
        const dx = neighbor.x - particle.x
        const dy = neighbor.y - particle.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        if (distance < mergedConfig.connectRadius) {
          const opacity = 1 - (distance / mergedConfig.connectRadius)
          ctx.globalAlpha = opacity * 0.5
          
          ctx.beginPath()
          ctx.moveTo(particle.x, particle.y)
          ctx.lineTo(neighbor.x, neighbor.y)
          ctx.stroke()
        }
      })
    })
    
    // Draw particles
    ctx.globalAlpha = 1
    ctx.fillStyle = colors.particle || 'rgba(0,0,0,0.5)'
    
    particlesRef.current.forEach(particle => {
      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)
      ctx.fill()
    })
    
    // Debug info
    if (mergedConfig.debug) {
      ctx.fillStyle = isDarkMode ? '#fff' : '#000'
      ctx.font = '12px monospace'
      ctx.fillText(`FPS: ${Math.round(1000 / performanceRef.current.avgFrameTime)}`, 10, 20)
      ctx.fillText(`Particles: ${particlesRef.current.length}`, 10, 35)
      ctx.fillText(`Grid cells: ${gridRef.current.size}`, 10, 50)
    }
  }, [getColors, updateSpatialGrid, getNeighbors, mergedConfig, isDarkMode])

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    
    const now = performance.now()
    const perf = performanceRef.current
    const deltaTime = now - perf.lastTime
    
    // Update performance metrics
    perf.frameCount++
    const frameTime = now - perf.lastTime
    perf.avgFrameTime = perf.avgFrameTime * 0.9 + frameTime * 0.1
    perf.lastTime = now
    
    // Update adaptive quality
    updateAdaptiveQuality()
    
    // Update and render
    updateParticles(canvas.width, canvas.height, deltaTime)
    render(ctx, canvas.width, canvas.height)
    
    // Continue animation
    if (!isReducedMotion) {
      animationRef.current = requestAnimationFrame(animate)
    }
  }, [render, updateParticles, updateAdaptiveQuality, isReducedMotion])

  // Setup canvas
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !canvasSupported) return
    
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = container.getBoundingClientRect()
      
      // Only resize if dimensions actually changed
      const newWidth = rect.width * dpr
      const newHeight = rect.height * dpr
      
      if (canvas.width === newWidth && canvas.height === newHeight) {
        return;
      }
      
      canvas.width = newWidth
      canvas.height = newHeight
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
      
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.scale(dpr, dpr)
        // Only reinitialize particles on actual resize, not on every re-render
        initParticles(rect.width, rect.height, true)
        
        // Render static frame for reduced motion
        if (isReducedMotion) {
          render(ctx, rect.width, rect.height)
        }
      }
    }
    
    resizeCanvas()
    
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)
    
    return () => {
      resizeObserver.disconnect()
    }
  }, [canvasSupported, initParticles, render, isReducedMotion])

  // Handle animation lifecycle
  useEffect(() => {
    if (!canvasSupported || isReducedMotion) return
    
    const startAnimation = () => {
      if (!animationRef.current) {
        performanceRef.current.lastTime = performance.now()
        animate()
      }
    }
    
    const stopAnimation = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = 0
      }
    }
    
    // Handle visibility change
    const handleVisibilityChange = () => {
      if (mergedConfig.pauseWhenHidden) {
        if (document.visibilityState === 'visible') {
          startAnimation()
        } else {
          stopAnimation()
        }
      }
    }
    
    startAnimation()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      stopAnimation()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [animate, canvasSupported, isReducedMotion, mergedConfig.pauseWhenHidden])

  // Fallback for no canvas support
  if (!canvasSupported) {
    return (
      <div 
        ref={containerRef}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
      </div>
    )
  }

  return (
    <div 
      ref={containerRef}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: getColors().bg }}
      />
    </div>
  )
}